import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════

const STORAGE_KEY = 'sk_onboarding_completed';
export const NEW_USER_KEY = 'sk_onboarding_new_user';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

interface TourStep {
  id: string;
  title: string;
  description: string;
  route?: string;
  target?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  spotlightPadding?: number;
  action?: () => Promise<void> | void; // Programmatic action to run before showing this step
}

// ═══════════════════════════════════════════════════════════════
// UTILS
// ═══════════════════════════════════════════════════════════════

const sleep = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

const waitForEl = (selector: string, timeout = 3000): Promise<Element | null> =>
  new Promise(resolve => {
    const el = document.querySelector(selector);
    if (el) {
      resolve(el);
      return;
    }
    const t0 = Date.now();
    const id = setInterval(() => {
      const found = document.querySelector(selector);
      if (found) {
        clearInterval(id);
        resolve(found);
      } else if (Date.now() - t0 > timeout) {
        clearInterval(id);
        resolve(null);
      }
    }, 80);
  });

// ═══════════════════════════════════════════════════════════════
// TOUR STEPS
// ═══════════════════════════════════════════════════════════════

// Helper: type text into an input with realistic key events
const typeInto = async (selector: string, text: string) => {
  const el = document.querySelector(selector) as HTMLInputElement;
  if (!el) return;
  el.focus();
  // Use native input setter to trigger React's onChange
  const nativeSet = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
  if (nativeSet) {
    nativeSet.call(el, text);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }
};

const STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to ScrapeKing',
    description:
      "Let's walk you through your first campaign — step by step. You'll see exactly how to find leads and start reaching customers.\n\nYou can skip anytime.",
  },
  {
    id: 'find-leads-card',
    title: 'This Is Your Starting Point',
    description:
      'The "Find Your Leads" card is where every campaign begins. Enter who you\'re looking for and where — then hit Search.\n\nLet me show you how it works...',
    route: '/dashboard',
    target: 'tour-find-leads',
    placement: 'left',
    spotlightPadding: 8,
  },
  {
    id: 'keyword-filled',
    title: 'Step 1: Enter Your Target',
    description:
      'I\'ve typed "attorney" as an example. You can search for any business type — dentists, restaurants, real estate agents, plumbers, you name it.\n\nScrapeKing automatically picks the best data sources for your search.',
    route: '/dashboard',
    target: 'tour-keyword',
    placement: 'left',
    action: async () => {
      typeInto('[data-tour="tour-keyword"]', 'attorney');
    },
  },
  {
    id: 'location-filled',
    title: 'Step 2: Pick a Location',
    description:
      'Now I\'ve entered "New York" as the location. You can search any city, state, or region.\n\nNext, I\'ll click Search to start building your campaign.',
    route: '/dashboard',
    target: 'tour-location',
    placement: 'left',
    action: async () => {
      typeInto('[data-tour="tour-location"]', 'New York');
    },
  },
  {
    id: 'tokens',
    title: 'Your Token Balance',
    description:
      'Before we search — see this? Tokens power your lead generation. Each lead costs tokens. You start with free tokens to try things out.\n\nTap the + button anytime to add more.',
    route: '/dashboard',
    target: 'tour-tokens',
    placement: 'bottom',
  },
  {
    id: 'plans',
    title: 'Upgrade Your Plan',
    description:
      "You're on the Free plan with limited tokens. These are your available packages — pick one that matches your outreach goals.\n\nMore tokens means more leads, more customers. Upgrade anytime.",
    route: '/dashboard',
    target: 'tour-credits-modal',
    placement: 'right',
    spotlightPadding: 4,
    action: async () => {
      // Open the credits modal by clicking the + button
      const wrapper = document.querySelector('[data-tour="tour-tokens"]');
      if (wrapper) {
        const btns = wrapper.querySelectorAll('button');
        const addBtn = btns[btns.length - 1] as HTMLElement;
        if (addBtn) addBtn.click();
      }
    },
  },
  {
    id: 'quick-campaign-location',
    title: "Let's Build Your Campaign",
    description:
      'I\'ve searched for "attorney" in "New York" and we\'re now on the campaign builder. The keyword is already confirmed.\n\nThis is step 2 — I\'m confirming the location for you. In your real campaigns, you\'ll type a city and pick from the suggestions.',
    route: '/quick-campaign?keywords=attorney&location=New+York',
    target: 'tour-qc-location',
    placement: 'right',
    action: async () => {
      // Wait for the city search input to appear, then type and press Enter
      const input = (await waitForEl('input[placeholder*="Type a city"]', 4000)) as HTMLInputElement | null;
      if (input) {
        input.focus();
        const nativeSet = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
        if (nativeSet) {
          nativeSet.call(input, 'New York');
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        // Wait for suggestions to appear
        await sleep(600);
        // Click the first suggestion if available
        const suggestion = document.querySelector('[class*="AutocompleteOption"]') as HTMLElement;
        if (suggestion) {
          suggestion.click();
          await sleep(400);
        } else {
          // Fallback: press Enter
          input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
          await sleep(400);
        }
      }
    },
  },
  {
    id: 'quick-campaign-leads',
    title: 'Step 3: How Many Leads?',
    description:
      'Choose how many leads to collect. Each lead costs 1 token. Start small with 100 — you can always run more campaigns later.\n\nThe slider shows different package sizes and their token cost.',
    route: '/quick-campaign',
    target: 'tour-qc-leads',
    placement: 'right',
  },
  {
    id: 'campaign-modes',
    title: 'Step 4: Choose Your Approach',
    description:
      "Here's the key decision — three campaign types:\n\nJust Collect — save contacts to your database for manual outreach.\n\nCollect & Email — find leads AND send personalized outreach automatically. Most popular.\n\nMaximum Outreach — emails plus website contact form submissions for 30-40% more reach.",
    route: '/quick-campaign',
    target: 'tour-modes',
    placement: 'right',
    spotlightPadding: 6,
  },
  {
    id: 'email-setup-purchase',
    title: 'Email Setup: The Easy Way',
    description:
      'If you chose email outreach, you need email accounts. This is the easy path — ScrapeKing handles domain purchase, DNS, email accounts, and warmup. Ready to send in 24-48 hours.',
    route: '/setup',
    target: 'tour-email-purchase',
    placement: 'right',
    spotlightPadding: 8,
  },
  {
    id: 'email-setup-own',
    title: 'Or: Bring Your Own Email',
    description:
      'Already have Gmail, Outlook, or other accounts? Connect them here for free. You handle domain and DNS setup, but warmup is still included.',
    route: '/setup',
    target: 'tour-email-own',
    placement: 'left',
    spotlightPadding: 8,
  },
  {
    id: 'finish',
    title: "You're Ready!",
    description:
      "That's the full workflow — search for leads, pick your campaign type, connect email, and launch.\n\nHead back to the dashboard and try it for real. Go get those customers!",
    route: '/dashboard',
  },
];

// ═══════════════════════════════════════════════════════════════
// ANIMATIONS
// ═══════════════════════════════════════════════════════════════

const fadeIn = keyframes`
  from { opacity: 0 }
  to { opacity: 1 }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.97) }
  to { opacity: 1; transform: translateY(0) scale(1) }
`;

const spotlightPulse = keyframes`
  0%, 100% {
    box-shadow:
      0 0 0 2px rgba(0, 200, 224, 0.4),
      0 0 20px rgba(0, 200, 224, 0.15),
      0 0 0 9999px rgba(0, 0, 0, 0.75);
  }
  50% {
    box-shadow:
      0 0 0 4px rgba(0, 200, 224, 0.6),
      0 0 30px rgba(0, 200, 224, 0.25),
      0 0 0 9999px rgba(0, 0, 0, 0.75);
  }
`;

// ═══════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  pointer-events: all;
`;

const CenterOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10001;
  background: rgba(0, 0, 0, 0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.3s ease;
  pointer-events: all;
  padding: 24px;
`;

const SpotlightBox = styled.div`
  position: fixed;
  z-index: 10001;
  border-radius: 12px;
  pointer-events: none;
  animation: ${spotlightPulse} 2.5s ease-in-out infinite;
  transition:
    top 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    left 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    height 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const tooltipStyles = css`
  background: linear-gradient(145deg, #151c24 0%, #0f1419 100%);
  border: 1px solid rgba(0, 200, 224, 0.35);
  border-radius: 16px;
  overflow: hidden;
  pointer-events: all;
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.5),
    0 0 40px rgba(0, 200, 224, 0.08);
`;

const FloatingTooltip = styled.div`
  position: fixed;
  z-index: 10002;
  width: 340px;
  max-width: calc(100vw - 32px);
  ${tooltipStyles}
`;

const CenterCard = styled.div`
  width: 440px;
  max-width: calc(100vw - 48px);
  ${tooltipStyles}
  animation: ${slideUp} 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const TooltipInner = styled.div`
  animation: ${slideUp} 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

const AccentBar = styled.div`
  height: 3px;
  background: linear-gradient(90deg, transparent, #00c8e0, transparent);
`;

const TooltipBody = styled.div`
  padding: 24px;
`;

const StepCounter = styled.div`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.7rem;
  font-weight: 500;
  color: #00c8e0;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 10px;
`;

const StepTitle = styled.h3`
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.15rem;
  font-weight: 600;
  color: #f0f4f8;
  margin: 0 0 12px;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const StepDescription = styled.p`
  font-family: 'Outfit', sans-serif;
  font-size: 0.875rem;
  line-height: 1.65;
  color: #94a3b8;
  margin: 0 0 20px;
  white-space: pre-line;
`;

const ProgressRow = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 20px;
`;

const ProgressDot = styled.div<{ $state: 'active' | 'done' | 'future' }>`
  height: 6px;
  border-radius: 3px;
  transition: all 0.3s ease;
  width: ${p => (p.$state === 'active' ? '20px' : '6px')};
  background: ${p =>
    p.$state === 'active' ? '#00c8e0' : p.$state === 'done' ? 'rgba(0, 200, 224, 0.45)' : 'rgba(100, 116, 139, 0.3)'};
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SkipBtn = styled.button`
  background: none;
  border: none;
  color: #64748b;
  font-family: 'Outfit', sans-serif;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 8px 4px;
  transition: color 0.2s;

  &:hover {
    color: #94a3b8;
  }
`;

const NavButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const BackBtn = styled.button`
  background: transparent;
  border: 1px solid rgba(148, 163, 184, 0.3);
  color: #94a3b8;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #94a3b8;
    color: #f0f4f8;
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const NextBtn = styled.button`
  background: linear-gradient(135deg, #00c8e0, #007d8f);
  border: none;
  color: #0a0e14;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 8px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: linear-gradient(135deg, #41e2f7, #00c8e0);
    box-shadow: 0 0 20px rgba(0, 200, 224, 0.3);
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

const OnboardingTour: React.FC = () => {
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [busy, setBusy] = useState(false);

  const navigate = useNavigate();
  const tipRef = useRef<HTMLDivElement>(null);
  const stepRef = useRef(0);
  const busyRef = useRef(false);
  const initRef = useRef(false);

  const step = STEPS[stepIdx];
  const centered = !step.target;

  // Keep refs in sync
  useEffect(() => {
    stepRef.current = stepIdx;
  }, [stepIdx]);

  // ── Activate for new users ──────────────────────────────────
  // initRef prevents React StrictMode double-fire.
  // No cleanup returned so the timeout survives StrictMode unmount/remount.
  useEffect(() => {
    if (initRef.current) return;
    const isNew = localStorage.getItem(NEW_USER_KEY) === 'true';
    const done = localStorage.getItem(STORAGE_KEY) === 'true';
    if (isNew && !done) {
      initRef.current = true;
      setTimeout(() => setActive(true), 1200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Dismiss / complete tour ─────────────────────────────────
  const dismiss = useCallback(() => {
    const closeBtn = document.querySelector('button[aria-label="Close modal"]') as HTMLElement;
    if (closeBtn) closeBtn.click();
    localStorage.setItem(STORAGE_KEY, 'true');
    localStorage.removeItem(NEW_USER_KEY);
    setActive(false);
    navigate('/dashboard');
  }, [navigate]);

  // ── ESC key ─────────────────────────────────────────────────
  useEffect(() => {
    if (!active) return;
    const fn = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [active, dismiss]);

  // ── Track target position via RAF ───────────────────────────
  useEffect(() => {
    if (!active || busy || !step.target) return;
    let rafId: number;
    const sel = `[data-tour="${step.target}"]`;
    const tick = () => {
      const el = document.querySelector(sel);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect(prev => {
          if (
            !prev ||
            Math.abs(prev.top - r.top) > 0.5 ||
            Math.abs(prev.left - r.left) > 0.5 ||
            Math.abs(prev.width - r.width) > 0.5 ||
            Math.abs(prev.height - r.height) > 0.5
          ) {
            return r;
          }
          return prev;
        });
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [active, stepIdx, busy, step.target]);

  // ── Tooltip positioning — always beside the spotlight, never on top ──
  useEffect(() => {
    if (!rect || centered) return;
    requestAnimationFrame(() => {
      const pad = step.spotlightPadding ?? 10;
      const gap = 20;
      const tw = 340;
      const th = tipRef.current?.offsetHeight || 260;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Spotlight bounds
      const sT = rect.top - pad;
      const sL = rect.left - pad;
      const sB = sT + rect.height + pad * 2;
      const sR = sL + rect.width + pad * 2;
      const sCy = (sT + sB) / 2;

      // Measure clear space on each side (no clamping — if it doesn't fit, skip it)
      const sides = [
        { id: 'right', fits: vw - sR - gap >= tw, t: Math.max(12, Math.min(sCy - th / 2, vh - th - 12)), l: sR + gap },
        { id: 'left', fits: sL - gap >= tw, t: Math.max(12, Math.min(sCy - th / 2, vh - th - 12)), l: sL - tw - gap },
        {
          id: 'bottom',
          fits: vh - sB - gap >= th,
          t: sB + gap,
          l: Math.max(12, Math.min((sL + sR) / 2 - tw / 2, vw - tw - 12)),
        },
        {
          id: 'top',
          fits: sT - gap >= th,
          t: sT - th - gap,
          l: Math.max(12, Math.min((sL + sR) / 2 - tw / 2, vw - tw - 12)),
        },
      ];

      // Prefer the step's declared side, then try others
      const preferred = step.placement || 'right';
      const ordered = [sides.find(s => s.id === preferred)!, ...sides.filter(s => s.id !== preferred)];

      // Pick the first side where the tooltip actually fits without overlapping
      const pick = ordered.find(s => s.fits);

      if (pick) {
        setPos({ top: pick.t, left: pick.l });
      } else {
        // Nothing fits cleanly — pin to whichever viewport corner is furthest from spotlight center
        const spotCx = (sL + sR) / 2;
        const useRight = spotCx < vw / 2;
        setPos({
          top: Math.max(12, Math.min(sCy - th / 2, vh - th - 12)),
          left: useRight ? vw - tw - 16 : 16,
        });
      }
    });
  }, [rect, step, centered, stepIdx]);

  // ── Navigate between steps ──────────────────────────────────
  const goTo = useCallback(
    async (idx: number) => {
      if (idx < 0 || idx >= STEPS.length || busyRef.current) return;
      busyRef.current = true;
      setBusy(true);

      const next = STEPS[idx];

      // Close any open modal before transitioning
      const closeBtn = document.querySelector('button[aria-label="Close modal"]') as HTMLElement;
      if (closeBtn) {
        closeBtn.click();
        await sleep(300);
      }

      // Navigate to the target route
      if (next.route) {
        navigate(next.route);
        await sleep(600);
      }

      // Run programmatic action (e.g. fill form fields)
      if (next.action) {
        await next.action();
        await sleep(400);
      }

      // Wait for target and scroll into view
      if (next.target) {
        const el = await waitForEl(`[data-tour="${next.target}"]`, 4000);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          await sleep(350);
          setRect(el.getBoundingClientRect());
        } else {
          setRect(null);
        }
      } else {
        setRect(null);
      }

      setStepIdx(idx);
      stepRef.current = idx;
      busyRef.current = false;
      setBusy(false);
    },
    [navigate],
  );

  const handleNext = useCallback(() => {
    const cur = stepRef.current;
    if (cur < STEPS.length - 1) goTo(cur + 1);
    else dismiss();
  }, [goTo, dismiss]);

  const handleBack = useCallback(() => {
    const cur = stepRef.current;
    if (cur > 0) goTo(cur - 1);
  }, [goTo]);

  // ── Don't render when inactive ──────────────────────────────
  if (!active) return null;

  const isFirst = stepIdx === 0;
  const isLast = stepIdx === STEPS.length - 1;
  const pad = step.spotlightPadding ?? 10;

  // ── Shared tooltip content ──────────────────────────────────
  const content = (
    <>
      <AccentBar />
      <TooltipBody>
        <StepCounter>
          Step {stepIdx + 1} of {STEPS.length}
        </StepCounter>
        <StepTitle>{step.title}</StepTitle>
        <StepDescription>{step.description}</StepDescription>
        <ProgressRow>
          {STEPS.map((s, i) => (
            <ProgressDot key={s.id} $state={i === stepIdx ? 'active' : i < stepIdx ? 'done' : 'future'} />
          ))}
        </ProgressRow>
        <ActionRow>
          <SkipBtn onClick={dismiss}>{isLast ? '' : 'Skip tour'}</SkipBtn>
          <NavButtons>
            {!isFirst && (
              <BackBtn onClick={handleBack} disabled={busy}>
                Back
              </BackBtn>
            )}
            <NextBtn onClick={handleNext} disabled={busy}>
              {isLast ? "Let's Go!" : isFirst ? 'Start Tour' : 'Next'}
            </NextBtn>
          </NavButtons>
        </ActionRow>
      </TooltipBody>
    </>
  );

  // ── RENDER: Centered (welcome / finish / missing target) ────
  if (centered || !rect) {
    return createPortal(
      <CenterOverlay
        data-testid="onboarding-tour"
        onClick={e => {
          if (e.target === e.currentTarget && !busy) dismiss();
        }}
      >
        <CenterCard onClick={e => e.stopPropagation()}>{content}</CenterCard>
      </CenterOverlay>,
      document.body,
    );
  }

  // ── RENDER: Spotlight mode ──────────────────────────────────
  return createPortal(
    <>
      <Backdrop data-testid="onboarding-tour" />
      <SpotlightBox
        style={{
          top: rect.top - pad,
          left: rect.left - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
        }}
      />
      <FloatingTooltip ref={tipRef} style={{ top: pos.top, left: pos.left }}>
        <TooltipInner key={step.id}>{content}</TooltipInner>
      </FloatingTooltip>
    </>,
    document.body,
  );
};

export default OnboardingTour;
